// Minimal browser-safe frontmatter parser (gray-matter pulls in Node Buffer
// which fails under Vite). Supports the YAML subset we need: scalars, ISO
// dates, booleans, numbers, and string arrays like `tags: ["a", "b"]`.
function parseFrontmatter(raw: string): { data: Record<string, unknown>; content: string } {
  const match = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/.exec(raw);
  if (!match) return { data: {}, content: raw };
  const [, fm, content] = match;
  const data: Record<string, unknown> = {};
  for (const line of fm.split(/\r?\n/)) {
    const m = /^([A-Za-z0-9_]+)\s*:\s*(.*)$/.exec(line);
    if (!m) continue;
    const key = m[1];
    let value: string = m[2].trim();
    // strip wrapping quotes
    if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
      value = value.slice(1, -1);
    }
    // array literal
    if (value.startsWith("[") && value.endsWith("]")) {
      const inner = value.slice(1, -1).trim();
      data[key] = inner
        ? inner.split(",").map((s) => s.trim().replace(/^["']|["']$/g, ""))
        : [];
      continue;
    }
    if (value === "true") { data[key] = true; continue; }
    if (value === "false") { data[key] = false; continue; }
    if (/^-?\d+(\.\d+)?$/.test(value)) { data[key] = Number(value); continue; }
    data[key] = value;
  }
  return { data, content: content.trim() };
}


import { AUTHORS, type Author } from "./authors";

// Cover images
import coverBestBrokers from "@/assets/blog/best-brokers-eu-uk-cover.jpg";
import coverBrokers3Way from "@/assets/blog/brokers-3way-cover.jpg";
import coverAIAnalysis from "@/assets/blog/ai-portfolio-analysis-cover.jpg";
import coverBestDividendStocks from "@/assets/blog/best-dividend-stocks-2026-cover.jpg";
import coverTrackerVsSpreadsheet from "@/assets/blog/portfolio-tracker-vs-spreadsheet-cover.jpg";
import coverCoastFire from "@/assets/blog/what-is-coast-fire-cover.jpg";
import coverAiPersonalFinance from "@/assets/blog/ai-personal-finance-cover.jpg";
import coverAiFinancialAdvisor from "@/assets/blog/ai-financial-advisor-cover.jpg";
import coverBestAiInvestingApps from "@/assets/blog/best-ai-investing-apps-cover.jpg";
import coverAiFinancialAnalysis from "@/assets/blog/ai-financial-analysis-cover.jpg";
import coverPassiveIncome from "@/assets/blog/how-to-build-passive-income-cover.jpg";
import coverFireTypes from "@/assets/blog/fire-types-compared-cover.jpg";
import coverAppleIphone from "@/assets/blog/apple-stock-iphone-history-cover.jpg";

export type CategorySlug = "brokers" | "ai-investing";

export interface Category {
  slug: CategorySlug;
  name: string;
  description: string;
}

export const CATEGORIES: Record<CategorySlug, Category> = {
  brokers: {
    slug: "brokers",
    name: "Brokers",
    description:
      "Neutral, methodology-driven comparisons of stock brokers available in Europe and the UK.",
  },
  "ai-investing": {
    slug: "ai-investing",
    name: "AI Investing",
    description:
      "How AI is changing portfolio research, analysis, and tracking for everyday investors.",
  },
};

export interface PostFrontmatter {
  slug: string;
  title: string;
  description: string;
  date: string; // ISO
  lastUpdated: string; // ISO
  author: string; // author id
  category: CategorySlug;
  tags: string[];
  cover: string; // imported asset path
  readingTime: number; // minutes
  featured?: boolean;
  seoTitle?: string;
  seoDescription?: string;
}

export interface Post extends PostFrontmatter {
  content: string;
  authorObj: Author;
}

// Cover map (frontmatter references by key, resolved here)
const COVERS: Record<string, string> = {
  "best-brokers-eu-uk-cover": coverBestBrokers,
  "brokers-3way-cover": coverBrokers3Way,
  "ai-portfolio-analysis-cover": coverAIAnalysis,
  "best-dividend-stocks-2026-cover": coverBestDividendStocks,
  "portfolio-tracker-vs-spreadsheet-cover": coverTrackerVsSpreadsheet,
  "what-is-coast-fire-cover": coverCoastFire,
  "ai-personal-finance-cover": coverAiPersonalFinance,
  "ai-financial-advisor-cover": coverAiFinancialAdvisor,
  "best-ai-investing-apps-cover": coverBestAiInvestingApps,
  "ai-financial-analysis-cover": coverAiFinancialAnalysis,
  "how-to-build-passive-income-cover": coverPassiveIncome,
  "fire-types-compared-cover": coverFireTypes,
  "apple-stock-iphone-history-cover": coverAppleIphone,
};

// 301-style client redirects for old slugs that were consolidated or replaced.
// Consumed by BlogPost.tsx — redirects to /blog/<new-slug>, which can be either
// a markdown post or a dedicated standalone route (e.g. retire-spoke pages).
export const POST_REDIRECTS: Record<string, string> = {
  "trading-212-vs-interactive-brokers":
    "trading-212-vs-trade-republic-vs-interactive-brokers",
  "degiro-vs-trading-212":
    "trading-212-vs-trade-republic-vs-interactive-brokers",
  "trade-republic-vs-lightyear":
    "trading-212-vs-trade-republic-vs-interactive-brokers",
  "how-much-money-to-retire-at-67": "how-much-to-retire-at-67",
};

// Eagerly load all markdown at build time.
const markdownFiles = import.meta.glob("./posts/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

function computeReadingTime(text: string): number {
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 220));
}

export const POSTS: Post[] = Object.entries(markdownFiles)
  .map(([path, raw]) => {
    const { data, content } = parseFrontmatter(raw);
    const fm = data as Partial<PostFrontmatter>;
    const slug = fm.slug ?? path.split("/").pop()!.replace(/\.md$/, "");
    const coverKey = (fm.cover as string) || `${slug}-cover`;
    const cover = COVERS[coverKey] ?? coverBestBrokers;
    return {
      slug,
      title: fm.title ?? slug,
      description: fm.description ?? "",
      date: fm.date ?? new Date().toISOString(),
      lastUpdated: fm.lastUpdated ?? fm.date ?? new Date().toISOString(),
      author: fm.author ?? "editorial",
      category: (fm.category as CategorySlug) ?? "ai-investing",
      tags: fm.tags ?? [],
      cover,
      readingTime: fm.readingTime ?? computeReadingTime(content),
      featured: fm.featured ?? false,
      seoTitle: fm.seoTitle,
      seoDescription: fm.seoDescription,
      content,
      authorObj: AUTHORS[fm.author ?? "editorial"] ?? AUTHORS.editorial,
    } as Post;
  })
  .sort((a, b) => +new Date(b.date) - +new Date(a.date));

export function getPostBySlug(slug: string): Post | undefined {
  return POSTS.find((p) => p.slug === slug);
}

export function getPostsByCategory(category: CategorySlug): Post[] {
  return POSTS.filter((p) => p.category === category);
}

export function getRelatedPosts(slug: string, limit = 3): Post[] {
  const current = getPostBySlug(slug);
  if (!current) return [];
  return POSTS.filter(
    (p) => p.slug !== slug && p.category === current.category,
  ).slice(0, limit);
}
